import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 무거운 집계 조회용 캐시 (데이터 분석 탭 · 관리자 통계 공용).
 *
 * 두 화면 다 react-query를 쓰지 않고 useState+useEffect로 직접 부른다 —
 * 무거운 집계 API에 창 포커스 refetch(react-query 기본값)가 붙으면 오히려 더 나쁘다.
 * 그런데 캐시가 아예 없어서 뷰 전환·필터 왕복마다 같은 쿼리가 새로 나갔고,
 * 답례품 전환처럼 서버가 1년치 fevent·이벤트로그를 훑는 엔드포인트는 그게 그대로
 * DB 부하가 됐다. 그래서 최소한의 것만 직접 둔다:
 *
 * - **키가 같으면 TTL 안에서 재사용** — 뷰 전환·재마운트로 재조회하지 않는다
 * - **같은 키 동시 요청은 하나로 합침** — 연타로 같은 집계가 N번 도는 것(스탬피드) 방지
 * - **키가 바뀌면 이전 응답은 버림** — 느린 옛 응답이 새 응답을 덮어쓰는 경합 차단
 *   (AbortController가 아니라서 서버 작업 자체는 계속되지만, 화면이 어긋나진 않는다)
 */

/** 기본 TTL. 집계 화면이라 분 단위로 굳어 있어도 판단이 달라지지 않는다 */
export const ANALYSIS_CACHE_TTL_MS = 5 * 60 * 1000;
/** 서버 창이 고정된(최근 1년 학습) 페이싱류 — 더 길게 잡아도 값이 거의 같다 */
export const PACING_CACHE_TTL_MS = 10 * 60 * 1000;

/** 필터 조합이 늘어나도 무한정 쌓이지 않게 (LRU까진 필요 없다) */
const MAX_ENTRIES = 40;

interface CacheEntry {
  at: number;
  value: unknown;
}

const store = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<unknown>>();

/** 선배선 필드/엔드포인트가 구서버에 없을 때 — 오류가 아니라 "아직 배포 전" 안내 */
export const isNotFoundError = (e: unknown) =>
  (e as { response?: { status?: number } })?.response?.status === 404;

/**
 * 사용자에게 보여줄 오류 문구. 우선순위 셋.
 *
 * 1. **400의 서버 message** — 서버가 조회 조건을 거절한 이유가 그대로 안내가 된다
 *    (예: `interval=day` 92일 상한 → "일 단위 조회는 최대 92일까지 가능합니다…").
 *    ⚠️ 전역 예외 필터가 `error`를 덮어써서 400 말고는 코드도 문구도 쓸 게 없다
 *    (`shopOrderCancelErrorMessage`와 같은 규칙).
 * 2. **로더가 손질한 Error의 message** — 호출부가 상태코드를 자기 문구로 바꿔 던진 경우
 *    (`toStatError`의 "조회 권한이 없습니다." 등). 이건 이미 사용자용 문구다.
 * 3. 그 외 — 폴백. 특히 **axios 원문은 절대 화면에 내보내지 않는다**
 *    ("Request failed with status code 500").
 */
export const cachedResourceErrorMessage = (e: unknown, fallback: string): string => {
  const err = e as {
    response?: { status?: number; data?: { message?: unknown } };
    isAxiosError?: boolean;
  };
  const response = err?.response;

  if (response?.status === 400) {
    const message = response.data?.message;
    //* class-validator는 message를 배열로 준다
    if (Array.isArray(message)) {
      const joined = message.filter((m) => typeof m === 'string').join(' ');
      if (joined) return joined;
    } else if (typeof message === 'string' && message) {
      return message;
    }
  }

  //* 로더가 만든 Error만 신뢰한다 — axios가 만든 것은 원문이라 쓸 수 없다
  if (e instanceof Error && !err.isAxiosError && e.message) return e.message;
  return fallback;
};

const peek = <T>(key: string, ttlMs: number): T | undefined => {
  const hit = store.get(key);
  if (!hit) return undefined;
  if (Date.now() - hit.at >= ttlMs) {
    store.delete(key);
    return undefined;
  }
  return hit.value as T;
};

const put = (key: string, value: unknown) => {
  store.set(key, { at: Date.now(), value });
  if (store.size > MAX_ENTRIES) {
    const oldest = Array.from(store.entries()).sort((a, b) => a[1].at - b[1].at)[0];
    if (oldest) store.delete(oldest[0]);
  }
};

/** 캐시 비우기. prefix를 주면 그 접두사 키만 (예: 'giftConversion|') */
export const dropAnalysisCache = (prefix?: string) => {
  if (!prefix) {
    store.clear();
    inflight.clear();
    return;
  }
  for (const key of Array.from(store.keys())) {
    if (key.startsWith(prefix)) store.delete(key);
  }
  for (const key of Array.from(inflight.keys())) {
    if (key.startsWith(prefix)) inflight.delete(key);
  }
};

/**
 * 캐시 히트면 그대로, 아니면 로더 1회 실행. 같은 키가 이미 날아가 있으면
 * 그 약속을 그대로 돌려준다 — 연타해도 서버 쪽 집계는 한 번만 돈다.
 */
export const loadCached = <T>(
  key: string,
  loader: () => Promise<T>,
  ttlMs: number = ANALYSIS_CACHE_TTL_MS,
): Promise<T> => {
  const hit = peek<T>(key, ttlMs);
  if (hit !== undefined) return Promise.resolve(hit);

  const running = inflight.get(key) as Promise<T> | undefined;
  if (running) return running;

  const task = loader()
    .then((value) => {
      put(key, value);
      return value;
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, task);
  return task;
};

export interface CachedResource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** 404 — 해당 API가 아직 이 서버에 배포되지 않음 */
  notReady: boolean;
  /** 캐시를 버리고 다시 조회 */
  refetch: () => void;
}

/**
 * 캐시·중복제거·경합가드가 붙은 조회 훅.
 *
 * ⚠️ 조회를 결정하는 건 `key` 하나다. loader는 렌더마다 새 함수라 의존성에 넣으면
 * 재조회가 끝없이 도므로 ref로 고정한다 — 호출부에서 useCallback으로 감쌀 필요 없다.
 */
export function useCachedResource<T>(
  key: string,
  loader: () => Promise<T>,
  options: { ttlMs?: number; errorMessage?: string; enabled?: boolean } = {},
): CachedResource<T> {
  const {
    ttlMs = ANALYSIS_CACHE_TTL_MS,
    errorMessage = '데이터를 불러올 수 없습니다.',
    //* 조회 전제(기간·선행 조회)가 아직 안 갖춰졌을 때 — 키를 억지로 비우는 대신 여기서 끈다
    enabled = true,
  } = options;

  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const [tick, setTick] = useState(0);
  const [state, setState] = useState<Omit<CachedResource<T>, 'refetch'>>(() => {
    const hit = enabled ? peek<T>(key, ttlMs) : undefined;
    return {
      data: hit ?? null,
      loading: enabled && hit === undefined,
      error: null,
      notReady: false,
    };
  });

  useEffect(() => {
    let alive = true;

    if (!enabled) {
      //* 꺼진 동안 로딩으로 남아 있으면 화면이 영원히 스피너다
      setState((prev) => (prev.loading ? { ...prev, loading: false } : prev));
      return;
    }

    const hit = peek<T>(key, ttlMs);
    if (hit !== undefined) {
      setState({ data: hit, loading: false, error: null, notReady: false });
      return;
    }

    // 이전 데이터는 남겨둔다 — 재조회 때 화면이 통째로 로딩으로 깜빡이지 않게
    setState((prev) => ({ ...prev, loading: true, error: null, notReady: false }));

    loadCached<T>(key, () => loaderRef.current(), ttlMs)
      .then((value) => {
        if (alive) {
          setState({ data: value, loading: false, error: null, notReady: false });
        }
      })
      .catch((e) => {
        if (!alive) return;
        if (isNotFoundError(e)) {
          setState({ data: null, loading: false, error: null, notReady: true });
        } else {
          setState({
            data: null,
            loading: false,
            error: cachedResourceErrorMessage(e, errorMessage),
            notReady: false,
          });
        }
      });

    return () => {
      // 키가 바뀌었거나 언마운트 — 이 응답은 더 이상 화면의 주인이 아니다
      alive = false;
    };
  }, [key, tick, ttlMs, errorMessage, enabled]);

  const refetch = useCallback(() => {
    store.delete(key);
    inflight.delete(key);
    setTick((t) => t + 1);
  }, [key]);

  return { ...state, refetch };
}
